import { useAuthContext } from '@experiences/util';
import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
} from 'react';
import { useSWRConfig } from 'swr';
import useSWRInfinite from 'swr/infinite';

import {
    type CodedDashboard,
    fetchCodedDashboardsPage,
} from '../../../../../services/governanceV2/CodedDashboardsService';
import { useTenantsContext } from '../../../../tenants/TenantsContextProvider';

const PINNED_PAGE_SIZE = 20;

/**
 * Plain-query prefixes `refresh` invalidates, because a pin toggle changes the library row and the
 * by-id read too. Paged (`useSWRInfinite`) queries are absent on purpose — a key filter cannot reach
 * them; each owns its refresh.
 */
const DASHBOARD_KEY_PREFIXES = new Set([
    'coded-dashboard',
    'deployed-dashboards-page',
]);

export interface UseCodedDashboardsResult {
    /** Pinned rows only, accumulated across the pages loaded so far. */
    pinned: CodedDashboard[];
    isLoading: boolean;
    error: string | null;
    /** More pinned rows exist on the server than we have asked for. */
    hasMore: boolean;
    loadMore: () => void;
    /** Re-reads this hook's pages and the plain dashboard queries. Paged ones own their refresh. */
    refresh: () => Promise<unknown>;
}

export const CodedDashboardsContext = createContext<UseCodedDashboardsResult | null>(null);

export function useCodedDashboardsContext(): UseCodedDashboardsResult {
    const ctx = useContext(CodedDashboardsContext);

    if (!ctx) {
        throw new Error('useCodedDashboardsContext must be used inside CodedDashboardsContext.Provider');
    }

    return ctx;
}

/**
 * The pinned dashboards, paged.
 */
export function useCodedDashboards(): UseCodedDashboardsResult {
    const { token } = useAuthContext();
    /*
     * The tenant is always the one the shell's selector shows, never a substitute — another tenant's
     * dashboards under a selector naming this one is a silent wrong answer.
     *
     * `isLoading` is the whole wait: the provider seeds `selectedTenant` from the shell's cached tenant
     * as soon as the tenant list lands, so there is nothing to track beyond the list arriving.
     */
    const {
        selectedTenant, isLoading: isResolvingTenant,
    } = useTenantsContext();
    const tenantId = selectedTenant.id;
    const { mutate: mutateAll } = useSWRConfig();

    const isReady = Boolean(token && tenantId);

    const {
        data, error, isLoading, isValidating, size, setSize, mutate: mutatePages,
    } = useSWRInfinite(
        (index: number) => (isReady ? [ 'coded-dashboards', tenantId, index ] as const : null),
        ([ , id, index ]) => fetchCodedDashboardsPage(id, {
            top: PINNED_PAGE_SIZE,
            skip: index * PINNED_PAGE_SIZE,
            pinnedOnly: true,
        }),
        {
            revalidateOnFocus: false,
            revalidateFirstPage: false,
        },
    );

    const pinned = useMemo(() => (data ?? []).flatMap(page => page.dashboards), [ data ]);
    const total = data?.[0]?.total ?? 0;

    /*
     * Compared against rows *asked for*, not rows returned: the mapper drops rows the platform gave no
     * routing name, so counting what came back would leave this true forever and load pages endlessly.
     */
    const hasMore = size * PINNED_PAGE_SIZE < total;

    const loadMore = useCallback(() => {
        if (hasMore && !isValidating) {
            setSize(current => current + 1);
        }
    }, [ hasMore, isValidating, setSize ]);

    /*
     * Two calls, because one cannot reach both. SWR's key-filter mutate skips `$inf$` keys by design,
     * so it never revalidates a `useSWRInfinite` hook — only that hook's own `mutate` does. The filter
     * covers the plain queries; `mutatePages` covers this hook's pages.
     */
    const refresh = useCallback(
        () => Promise.all([
            mutateAll(key => Array.isArray(key)
                && typeof key[0] === 'string'
                && DASHBOARD_KEY_PREFIXES.has(key[0])),
            mutatePages(),
        ]),
        [ mutateAll, mutatePages ],
    );

    return {
        pinned,
        isLoading: !token || isResolvingTenant || isLoading,
        error: error ? (error.message ?? 'Failed to fetch dashboards') : null,
        hasMore,
        loadMore,
        refresh,
    };
}

export const CodedDashboardsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const value = useCodedDashboards();
    return (
        <CodedDashboardsContext.Provider value={value}>
            {children}
        </CodedDashboardsContext.Provider>
    );
};
